import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { EllipsisOutlined } from '@ant-design/icons'
import { Button, Modal, PageHeader, Popconfirm, Popover, Space, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import moment from 'moment'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import routes from '@/constants/routes'
import { Machine, MachinePart, MachinePartHistory } from '@/types'
import { formatHm, formatYMD } from '@/utils/date'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { CustomRowTable } from './styles'

type Props = {
  machineId: number
  dataSource: MachinePart[] | undefined
  dataSourceDetail: Machine[]
  dataSourceRecentHistory: MachinePartHistory[]
  getMachinePartsLoading: boolean
  onDelete: (id: MachinePart['id'], machineId: number) => void
  onCreateWashingHistory: (id: MachinePart['id'], machineId: number) => void
}

const MachinePartsTable = ({
  machineId,
  dataSource,
  dataSourceDetail,
  dataSourceRecentHistory,
  getMachinePartsLoading,
  onDelete,
  onCreateWashingHistory,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false)
  const [historyData, setHistoryData] = useState<MachinePartHistory[]>()

  const showHistoryModal = (machinePartId: number) => {
    const machinePart = dataSource?.find((part) => part.id === machinePartId)
    setHistoryData(machinePart?.washingHistories?.sort((a, b) => b.createdAt.localeCompare(a.createdAt)))
    setIsHistoryModalVisible(true)
  }

  const getStatusTag = (isWorking: boolean) => {
    const status = isWorking
      ? {
          color: 'green',
          text: t('components.pages.machines.list.table.working.inService'),
          icon: <CheckCircleOutlined />,
        }
      : { color: 'red', text: t('components.pages.machines.list.table.working.idle'), icon: <CloseCircleOutlined /> }

    return (
      <Tag color={status.color}>
        {status.icon} {status.text}
      </Tag>
    )
  }

  const handleCloseModal = () => setIsHistoryModalVisible(false)

  const machineColumns: ColumnsType<Machine> = [
    {
      title: t('components.pages.machines.list.table.name'),
      dataIndex: 'name',
      key: 'name',
      render: (record: Machine) => {
        return <CustomRowTable type="left">{record}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machines.list.table.typeName'),
      dataIndex: 'typeName',
      key: 'typeName',
      render: (typeName: number) => {
        return <CustomRowTable type="left">{typeName}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machines.list.table.status'),
      dataIndex: 'isWorking',
      key: 'isWorking',
      render: (isWorking: boolean) => {
        return <CustomRowTable type="left">{getStatusTag(isWorking)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machines.list.table.startedTime'),
      dataIndex: 'startedAt',
      key: 'startedAt',
      render: (startedAt: string) => {
        if (!startedAt) return <CustomRowTable type="left" />
        return <CustomRowTable type="left">{`${formatYMD(startedAt)} ${formatHm(startedAt)}`}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machines.list.table.finishedTime'),
      key: 'finishedTime',
      render: (record: Machine) => {
        if (!record?.startedAt) return <CustomRowTable type="left" />
        const finishedTime = moment(record.startedAt).add(record.laundryTime, 'minutes').format('YYYY/MM/DD HH:mm')
        return <CustomRowTable type="left">{finishedTime}</CustomRowTable>
      },
      align: 'center' as const,
    },
  ]

  const columns: ColumnsType<MachinePart> = [
    {
      title: t('components.pages.machineParts.list.table.name'),
      render: (record: MachinePart) => {
        return <CustomRowTable type="left">{record.washingMachinePart?.name}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machineParts.list.table.totalTimeUsed'),
      dataIndex: 'totalTimeUsed',
      key: 'totalTimeUsed',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machineParts.list.table.totalLife'),
      dataIndex: 'totalLife',
      key: 'totalLife',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machineParts.list.table.usedTime'),
      dataIndex: 'timesUsed',
      key: 'timesUsed',
      render: (record: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
      },
      align: 'center' as const,
    },
    {
      title: t('components.pages.machineParts.list.table.changedDate'),
      dataIndex: 'replacementDate',
      key: 'replacementDate',
      render: (replacementDate: string) => {
        return (
          <CustomRowTable type="left">
            {replacementDate ? `${formatYMD(replacementDate)} ${formatHm(replacementDate)}` : ''}
          </CustomRowTable>
        )
      },
      align: 'center' as const,
    },
  ]

  columns.push({
    align: 'left' as const,
    render: (text: string, record: MachinePart) => {
      return (
        <Space size="middle">
          <Popover
            placement="bottomRight"
            content={
              <Space size="small" direction="vertical">
                <Button size="small" onClick={() => showHistoryModal(record.id)}>
                  {t('components.pages.machineParts.list.table.detail')}
                </Button>
                <Button size="small" onClick={() => goToEditMachinePartPage(record.id, machineId)}>
                  {t('components.pages.admins.list.table.update')}
                </Button>
                <Popconfirm
                  id={record.id.toString()}
                  visible={!!visibleConfirmDelete[record.id]}
                  title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                  onConfirm={() => {
                    onDelete(record.id, machineId)
                  }}
                  onCancel={() => setVisibleConfirmDelete({})}
                >
                  <Button
                    size="small"
                    danger
                    onClick={() => setVisibleConfirmDelete({ [record.id]: true })}
                    loading={false}
                  >
                    {t('components.pages.admins.list.table.delete')}
                  </Button>
                </Popconfirm>
                {record.isWarning && (
                  <Button
                    size="small"
                    onClick={() => {
                      onCreateWashingHistory(record.id, machineId)
                    }}
                    type="dashed"
                    style={{ color: '#fff', background: '#000' }}
                  >
                    {t('components.pages.machineParts.list.table.complete')}
                  </Button>
                )}
              </Space>
            }
          >
            <Button>
              <EllipsisOutlined />
            </Button>
          </Popover>
        </Space>
      )
    },
  })

  const historyColumns: ColumnsType<MachinePartHistory> = [
    {
      title: t('components.pages.machineParts.list.recentHistory.name'),
      align: 'center' as const,
      render: (record: MachinePartHistory) => (
        <CustomRowTable type="left">{record.washingPart?.washingMachinePart?.name}</CustomRowTable>
      ),
    },
    {
      title: t('components.pages.machineParts.list.recentHistory.usedTime'),
      dataIndex: 'timesUsed',
      key: 'timesUsed',
      width: 150,
      align: 'center' as const,
      render: (text: string) => <CustomRowTable type="right">{formatJapaneseNumber(text)}</CustomRowTable>,
    },
    {
      title: t('components.pages.machineParts.list.recentHistory.changedDate'),
      dataIndex: 'replacementDate',
      key: 'replacementDate',
      align: 'center',
      render: (replacementDate: string) => (
        <CustomRowTable type="left">
          {replacementDate ? `${formatYMD(replacementDate)} ${formatHm(replacementDate)}` : ''}
        </CustomRowTable>
      ),
    },
  ]

  const goToEditMachinePartPage = (id: MachinePart['id'], machineId: number) => {
    const path = routes.updateMachineParts.path
      .replace(':machineId', machineId.toString())
      .replace(':id', id.toString())
    navigate(path)
  }

  return (
    <>
      <Table rowKey="id" bordered dataSource={dataSourceDetail} columns={machineColumns} pagination={false} />
      <div>
        <PageHeader
          ghost={false}
          title={`${t('components.pages.machineParts.list.table.title')}`}
          extra={
            <Button
              key="action"
              type="primary"
              onClick={() => {
                const path = routes.createMachineParts.path.replace(':id', machineId.toString())
                navigate(path)
              }}
            >
              {t('components.pages.detergentHistories.list.pageHeader.new')}
            </Button>
          }
        />
        <Table
          rowKey="id"
          bordered
          dataSource={dataSource}
          columns={columns}
          loading={getMachinePartsLoading}
          pagination={false}
          scroll={{ y: TABLE_MAX_HEIGHT }}
        />
        <Modal
          title={t('components.pages.machineParts.list.modal.title')}
          visible={isHistoryModalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={600}
        >
          <Table
            rowKey="id"
            bordered
            dataSource={historyData}
            pagination={false}
            columns={[
              {
                title: t('components.pages.machineParts.list.modal.usedTime'),
                dataIndex: 'timesUsed',
                key: 'timesUsed',
                align: 'center',
                render: (record: number) => {
                  return <CustomRowTable type="right">{formatJapaneseNumber(record)}</CustomRowTable>
                },
              },
              {
                title: t('components.pages.machineParts.list.modal.changedDate'),
                dataIndex: 'createdAt',
                key: 'createdAt',
                align: 'center',
                render: (createdAt: string) => (
                  <CustomRowTable type="left">
                    {createdAt ? `${formatYMD(createdAt)} ${formatHm(createdAt)}` : ''}
                  </CustomRowTable>
                ),
              },
            ]}
          />
        </Modal>
      </div>

      <div>
        <PageHeader ghost={false} title={`${t('components.pages.machineParts.list.recentHistory.title')}`} />
        <Table
          rowKey="id"
          bordered
          dataSource={dataSourceRecentHistory}
          columns={historyColumns}
          loading={getMachinePartsLoading}
          pagination={false}
          scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
        />
      </div>
    </>
  )
}

MachinePartsTable.displayName = 'MachinePartsTable'
export default MachinePartsTable
